import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiLock, FiCpu, FiSave, FiCheck, FiAlertCircle, FiX, FiSettings, FiCamera } = FiIcons;

const SettingsModal = ({ isOpen, onClose }) => {
    const { user, updateUserProfile, uploadUserAvatar, changePassword } = useAuth();
    const { success: showSuccess, error: showError } = useToast();
    const [activeTab, setActiveTab] = useState('profile');
    const [successMsg, setSuccessMsg] = useState('');
    const modalRef = useRef(null);
    const fileInputRef = useRef(null);

    const {
      state: saveState,
      error: saveError,
      handleAction: handleSaveAction,
      reset: resetSaveState,
      icon: saveIcon,
      text: saveText,
      classes: saveClasses,
    } = useButtonState({
      idleText: 'Save Changes',
      loadingText: 'Saving...',
      successText: 'Saved!',
      failedText: 'Failed',
      idleIcon: FiSave,
      onSuccess: () => {
        setSuccessMsg('Saved!');
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to save changes';
        setSuccessMsg('');
      },
      autoResetDelay: 2000,
    });

    // Form States
    const [profileData, setProfileData] = useState({
        name: user?.display_name || user?.user_metadata?.name || '',
        email: user?.email || '',
        title: 'Prompt Enthusiast'
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        if (user) {
            setProfileData(prev => ({
                ...prev,
                name: user.display_name || user.user_metadata?.name || prev.name,
                email: user.email || prev.email
            }));
        }
    }, [user]);

    // Close on click outside - REMOVED per requirement
    /*
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);
    */

    const handleSave = async () => {
        await handleSaveAction(async () => {
            if (activeTab === 'profile') {
                const { error } = await updateUserProfile({ display_name: profileData.name });
                 if (error) throw new Error(error.message);
            } else if (activeTab === 'security') {
                if (passwordData.newPassword !== passwordData.confirmPassword) {
                    setSuccessMsg('Passwords do not match');
                    setTimeout(() => setSuccessMsg(''), 2000);
                    return;
                }
                await changePassword(passwordData.currentPassword, passwordData.newPassword);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            }
        });
    };

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        await handleSaveAction(async () => {
            const MAX_SIZE = 1024 * 1024;
            let fileToUpload = file;

            if (file.size > MAX_SIZE) {
                fileToUpload = await compressImage(file, MAX_SIZE);
            }

             const { error } = await uploadUserAvatar(fileToUpload);
              if (error) throw new Error(error.message);
        });

        if (saveState === 'success') {
            setSuccessMsg('Avatar updated!');
            setTimeout(() => setSuccessMsg(''), 2000);
        }
    };

    const compressImage = (file, MAX_SIZE) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    let width = img.width;
                    let height = img.height;
                    const MAX_DIMENSION = 256;

                    if (width > height) {
                        if (width > MAX_DIMENSION) {
                            height *= MAX_DIMENSION / width;
                            width = MAX_DIMENSION;
                        }
                    } else {
                        if (height > MAX_DIMENSION) {
                            width *= MAX_DIMENSION / height;
                            height = MAX_DIMENSION;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);

                    let quality = 0.85;
                    let dataUrl = canvas.toDataURL('image/jpeg', quality);

                    while (dataUrl.length > MAX_SIZE && quality > 0.1) {
                        quality -= 0.1;
                        dataUrl = canvas.toDataURL('image/jpeg', quality);
                    }

                    canvas.toBlob((blob) => {
                        if (blob) {
                            const compressedFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now()
                            });
                            resolve(compressedFile);
                        } else {
                            reject(new Error('Failed to compress image'));
                        }
                    }, 'image/jpeg', quality);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    };
    
    const getAvatarUrl = (u) => {
        if (u?.profile_image) {
            return u.profile_image;
        }
        if (u?.avatar_url) {
            if (u.avatar_url.startsWith('http')) return u.avatar_url;
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            return `${apiUrl}${u.avatar_url}`;
        }
        return null;
    };


    if (!isOpen) return null;

    const tabs = [
        { id: 'profile', label: 'Profile', icon: FiUser },
        { id: 'security', label: 'Password', icon: FiLock },
    ];

    return (
        <div className="fixed inset-0 z-[100] md:flex md:items-center md:justify-center p-0 md:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div ref={modalRef} className="bg-white border-2 border-black shadow-neo-xl w-full md:max-w-4xl h-full md:h-[80vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="neo-window-header bg-neo-yellow flex-shrink-0">
                    <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiSettings} size={20} />
                        <h2 className="text-lg font-black uppercase">Account Settings</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 min-w-[44px] min-h-[44px] touch-target-sm hover:bg-red-400 hover:text-white border border-transparent hover:border-black transition-all"
                    >
                        <SafeIcon icon={FiX} size={20} />
                    </button>
                </div>

                 {/* Body */}
                 <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0 pb-safe">

                     {/* Sidebar - Horizontal scroll on mobile, vertical on desktop */}
                     <div className="w-full md:w-64 bg-gray-50 border-b-2 md:border-b-0 md:border-r-2 border-black p-4 flex-shrink-0 overflow-x-auto md:overflow-y-auto">
                         <div className="flex md:flex-col gap-2 min-w-max md:min-w-0 space-y-0 md:space-y-2">
                             {tabs.map(tab => (
                                 <button
                                     key={tab.id}
                                     onClick={() => setActiveTab(tab.id)}
                                     className={`flex items-center space-x-3 px-4 py-3 text-sm font-bold uppercase transition-all border-2 min-w-fit md:w-full ${
                                         activeTab === tab.id
                                         ? 'bg-black text-white border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,0.2)] translate-x-[2px]'
                                         : 'bg-white text-black border-black hover:bg-gray-200 shadow-neo-sm'
                                     }`}
                                 >
                                     <SafeIcon icon={tab.icon} size={16} />
                                     <span>{tab.label}</span>
                                 </button>
                             ))}
                         </div>
                     </div>

                     {/* Content Area */}
                     <div className="flex-1 p-6 md:p-8 overflow-y-auto bg-white">
                         {successMsg && (
                             <div className="mb-6 p-3 bg-green-100 border-2 border-green-500 text-green-800 font-bold flex items-center text-sm shadow-neo-sm">
                                 <SafeIcon icon={FiCheck} className="mr-2" /> {successMsg}
                             </div>
                         )}
                         {saveError && (
                             <div className="mb-6 p-3 bg-red-100 border-2 border-red-500 text-red-800 font-bold flex items-center text-sm shadow-neo-sm">
                                 <SafeIcon icon={FiAlertCircle} className="mr-2" /> {saveError}
                             </div>
                         )}

                         {/* Profile Tab */}
                         {activeTab === 'profile' && (
                             <div className="space-y-6 max-w-md animate-in slide-in-from-right-4 duration-300">
                                 <h3 className="text-xl font-black uppercase border-b-4 border-neo-blue inline-block mb-4">Your Profile</h3>

                                 <div className="flex items-center space-x-4 mb-6">
                                     <div
                                         onClick={handleAvatarClick}
                                         className="relative group h-16 w-16 min-w-[44px] min-h-[44px] bg-black text-white flex items-center justify-center border-2 border-black cursor-pointer overflow-hidden"
                                     >
                                         {getAvatarUrl(user) ? (
                                             <img src={getAvatarUrl(user)} alt="Profile" className="w-full h-full object-cover" />
                                         ) : (
                                             <span className="text-2xl font-black">
                                                 {(profileData.name?.[0] || 'U').toUpperCase()}
                                             </span>
                                         )}

                                         {/* Overlay */}
                                         <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all duration-200">
                                             <SafeIcon icon={FiCamera} className="text-white" size={20} />
                                         </div>
                                     </div>
                                     <input
                                         type="file"
                                         ref={fileInputRef}
                                         onChange={handleFileChange}
                                         accept="image/*"
                                         className="hidden"
                                     />

                                     <div>
                                         <p className="font-bold uppercase text-sm">{profileData.name || 'User'}</p>
                                         <p className="text-xs text-gray-500 font-mono">{profileData.email}</p>
                                     </div>
                                 </div>

                                 <div>
                                     <label className="block text-xs font-bold uppercase mb-1">Display Name</label>
                                     <input
                                         type="text"
                                         value={profileData.name}
                                         onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                                         className="neo-input input-touch"
                                     />
                                 </div>
                                 <div>
                                     <label className="block text-xs font-bold uppercase mb-1">Role / Title</label>
                                     <input
                                         type="text"
                                         value={profileData.title}
                                         onChange={(e) => setProfileData({...profileData, title: e.target.value})}
                                         className="neo-input input-touch"
                                     />
                                 </div>
                             </div>
                         )}

                         {/* Security Tab */}
                         {activeTab === 'security' && (
                              <div className="space-y-6 max-w-md animate-in slide-in-from-right-4 duration-300">
                                 <h3 className="text-xl font-black uppercase border-b-4 border-neo-red inline-block mb-4">Security</h3>

                                 <div className="flex items-start p-3 bg-red-50 border-l-4 border-red-500 text-red-900 mb-4">
                                     <SafeIcon icon={FiAlertCircle} className="mt-0.5 mr-2 flex-shrink-0" />
                                     <p className="text-xs font-medium">Changing your password will log you out of other sessions.</p>
                                 </div>

                                 <div>
                                     <label className="block text-xs font-bold uppercase mb-1">Current Password</label>
                                     <input
                                         type="password"
                                         value={passwordData.currentPassword}
                                         onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                         className="neo-input input-touch"
                                         placeholder="••••••••"
                                     />
                                 </div>
                                 <div>
                                     <label className="block text-xs font-bold uppercase mb-1">New Password</label>
                                     <input
                                         type="password"
                                         value={passwordData.newPassword}
                                         onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                         className="neo-input input-touch"
                                         placeholder="••••••••"
                                     />
                                 </div>
                                 <div>
                                     <label className="block text-xs font-bold uppercase mb-1">Confirm New Password</label>
                                     <input
                                         type="password"
                                         value={passwordData.confirmPassword}
                                         onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                         className="neo-input input-touch"
                                         placeholder="••••••••"
                                     />
                                 </div>
                             </div>
                         )}
                     </div>
                 </div>

                 {/* Footer */}
                 <div className="p-4 border-t-2 border-black bg-gray-50 flex justify-end">
                     <button
                         onClick={handleSave}
                         disabled={saveState === 'loading'}
                         className={`neo-btn neo-btn-primary flex items-center px-6 py-2 text-sm ${saveClasses}`}
                     >
                         {saveIcon && <SafeIcon icon={saveIcon} className="mr-2" size={16} />}
                         <span>{saveText}</span>
                     </button>
                 </div>
            </div>
        </div>
    );
};

export default SettingsModal;
